import { IconPlusRounded, IconTrash } from "@aksara-ui/icons";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  IconButton,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
  theme,
} from "@aksara-ui/react";
import { Field, FieldArray, FieldProps, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeCatalogStock } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  catalogId: string;
  type: string;
}

const initialValues = {
  quantity: "",
  voucherList: [
    {
      uniqueCode: "",
      pin: "",
      expiryDate: "",
    },
  ],
};

const CatalogStockAddModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  catalogId,
  type,
}) => {
  const dispatch = useDispatch();
  const [catalogType, setCatalogType] = useState("");

  const { catalogs, catalogDetail, isFormLoading } = useSelector(
    (state: any) => state.catalogReducer
  );
  const { vouchers } = useSelector((state: any) => state.voucherReducer);

  const handleCloseModal = () => {
    onClose && onClose();
    setCatalogType("");
  };

  const handleSubmit = (values: any) => {
    const { quantity, voucherList } = values;

    const uniqueCodes: string[] = [];
    const pins: string[] = [];
    const expiryDates: string[] = [];

    voucherList.forEach(
      (value: { uniqueCode: string; pin: string; expiryDate: string }) => {
        uniqueCodes.push(value.uniqueCode);
        pins.push(value.pin);
        expiryDates.push(value.expiryDate);
      }
    );

    dispatch(
      storeCatalogStock(
        catalogId,
        { catalogId, quantity, uniqueCodes, pins, expiryDates },
        handleCloseModal
      ) as any
    );
  };

  useEffect(() => {
    if (isOpen) {
      if (type === "Catalog") {
        const productDetail = !catalogs.length
          ? catalogDetail
          : catalogs.find((catalog: any) => catalog.id === parseInt(catalogId));
        setCatalogType(productDetail.catalog_type);
      }
      if (type === "Voucher") {
        const voucherDetail = !vouchers.length
          ? catalogDetail
          : vouchers.find((voucher: any) => voucher.id === parseInt(catalogId));
        setCatalogType(voucherDetail.catalog_type);
      }
    }
  }, [isOpen, catalogs, catalogId]);

  const validationSchema = yup.object({
    quantity: yup
      .number()
      .when([], () =>
        catalogType === "auto"
          ? yup.number().required("Quantity is required field")
          : yup.number().notRequired()
      ),
    voucherList: yup.array().of(
      yup.object().shape({
        uniqueCode: yup
          .string()
          .when([], () =>
            catalogType === "manual"
              ? yup.string().required("Unique Code is required field")
              : yup.string().notRequired()
          ),
        pin: yup.string(),
        expiryDate: yup
          .string()
          .when([], () =>
            catalogType === "manual"
              ? yup.date().required("Expiry Date is required field")
              : yup.date().notRequired()
          ),
      })
    ),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      minWidth={catalogType === "auto" ? 420 : 780}
      overflowY={"auto"}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>Add {type} Stock</Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  {catalogType === "auto" ? (
                    <Field name="quantity">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="quantity">Quantity</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="number"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : catalogType === "manual" ? (
                    <FieldArray name="voucherList">
                      {({ remove, push }) => {
                        return (
                          <Box>
                            <Box
                              display={"flex"}
                              justifyContent={"left"}
                              paddingBottom={20}
                            >
                              <Button
                                size={32}
                                variant="primary"
                                borderRadius={20}
                                style={{
                                  fontWeight: 600,
                                  fontSize: 12,
                                  gap: 5,
                                }}
                                display={"flex"}
                                alignItems={"center"}
                                iconPosition="left"
                                onClick={() =>
                                  push({
                                    uniqueCode: "",
                                    pin: "",
                                    expiryDate: "",
                                  })
                                }
                              >
                                <IconPlusRounded size={20} fill={"white"} /> Add
                                Unique Code
                              </Button>
                            </Box>
                            {values.voucherList.length > 0 &&
                              values.voucherList.map((uniqCode, index) => {
                                return (
                                  <Box
                                    display={"flex"}
                                    flexDirection={"row"}
                                    alignItems={"center"}
                                    style={{ gap: 15 }}
                                    paddingBottom={10}
                                  >
                                    <Box
                                      display={"flex"}
                                      flexDirection={"row"}
                                      width={"95%"}
                                      style={{ gap: 15 }}
                                    >
                                      <Field
                                        name={`voucherList.${index}.uniqueCode`}
                                        key={index}
                                      >
                                        {({
                                          field,
                                          meta,
                                        }: FieldProps<string>) => (
                                          <Box
                                            paddingBottom={10}
                                            width={"100%"}
                                          >
                                            <FormLabel
                                              htmlFor={`voucherList.${index}.uniqueCode`}
                                            >
                                              Unique Code
                                            </FormLabel>

                                            <InputGroup inputSize={40}>
                                              <InputText
                                                type="text"
                                                size={40}
                                                errors={
                                                  !!meta.error && meta.touched
                                                }
                                                {...field}
                                              />
                                            </InputGroup>

                                            {!!meta.error && meta.touched && (
                                              <InputMessage variant="error">
                                                {meta.error}
                                              </InputMessage>
                                            )}
                                          </Box>
                                        )}
                                      </Field>

                                      <Field
                                        name={`voucherList.${index}.pin`}
                                        key={index}
                                      >
                                        {({
                                          field,
                                          meta,
                                        }: FieldProps<string>) => (
                                          <Box
                                            paddingBottom={10}
                                            width={"100%"}
                                          >
                                            <FormLabel
                                              htmlFor={`voucherList.${index}.pin`}
                                            >
                                              Pin
                                            </FormLabel>

                                            <InputGroup inputSize={40}>
                                              <InputText
                                                type="password"
                                                size={40}
                                                errors={
                                                  !!meta.error && meta.touched
                                                }
                                                {...field}
                                              />
                                            </InputGroup>

                                            {!!meta.error && meta.touched && (
                                              <InputMessage variant="error">
                                                {meta.error}
                                              </InputMessage>
                                            )}
                                          </Box>
                                        )}
                                      </Field>

                                      <Field
                                        name={`voucherList.${index}.expiryDate`}
                                        key={index}
                                      >
                                        {({
                                          field,
                                          meta,
                                        }: FieldProps<string>) => (
                                          <Box
                                            paddingBottom={10}
                                            width={"100%"}
                                          >
                                            <FormLabel
                                              htmlFor={`voucherList.${index}.expiryDate`}
                                            >
                                              Expiry Date
                                            </FormLabel>

                                            <InputGroup inputSize={40}>
                                              <InputText
                                                type="date"
                                                size={40}
                                                errors={
                                                  !!meta.error && meta.touched
                                                }
                                                {...field}
                                              />
                                            </InputGroup>

                                            {!!meta.error && meta.touched && (
                                              <InputMessage variant="error">
                                                {meta.error}
                                              </InputMessage>
                                            )}
                                          </Box>
                                        )}
                                      </Field>
                                    </Box>

                                    <IconButton
                                      type="button"
                                      width={"10%"}
                                      onClick={() => remove(index)}
                                      disabled={index !== 0 ? false : true}
                                    >
                                      <IconTrash
                                        fill={
                                          index !== 0
                                            ? theme.colors.red06
                                            : theme.colors.grey05
                                        }
                                      />
                                    </IconButton>
                                  </Box>
                                );
                              })}
                          </Box>
                        );
                      }}
                    </FieldArray>
                  ) : (
                    ""
                  )}
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    Add {type} Stock
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CatalogStockAddModal;
